<script setup lang="ts">
import { ref } from 'vue';
import { VDataTableServer } from 'vuetify/components';

import { session, supabase } from '@/src/supabase';

export interface Item {
  id: string;
  title: string;

  study_instance_uid?: string;
  user: string;
  userMeta: { card: { fn: string } };
  org: string;
  orgMeta: { person: { card: { fn: string } } };

  created_at: string;
}

const props = defineProps({
  studyInstanceUid: {
    type: String,
    required: true,
  },
  userUid: {
    type: String,
  },
});

const emit = defineEmits<{
  clicked: [item: Item];
  removeClicked: [item: Item];
}>();

const items = ref<any[]>([]);
const isLoading = ref(true);

const headers = [
  { title: 'Name', value: 'title', sortable: true },
  { title: 'Created at', value: 'created_at', sortable: true },
  { title: 'User', value: 'userMeta.card.fn', align: 'start', sortable: true },
  {
    title: 'Organization',
    value: 'orgMeta.person.card.fn',
    align: 'start',
    sortable: true,
  },
  { title: 'Actions', key: 'actions', align: 'end', sortable: false },
] as const;

async function reload() {
  try {
    isLoading.value = true;
    const query = supabase.from('analysis_state').select(
      `id,
      ${!props.studyInstanceUid ? 'study_instance_uid,' : ''}
      user,
      userMeta:person!analysis_state_user_fkey(card),
      org,
      orgMeta:org!analysis_state_org_fkey(
        person(card)
      ),
      state->title,
      created_at
      `,
    );
    if (props.studyInstanceUid) {
      query.eq('study_instance_uid', props.studyInstanceUid);
    }
    if (props.userUid) {
      query.eq('user', props.userUid);
    }
    query.order('created_at', { ascending: false });
    const { data, error } = await query;
    if (error) {
      throw error;
    }
    items.value = data;
  }
  catch (error) {
    console.error('Error fetching items:', error);
  }
  finally {
    isLoading.value = false;
  }
}

// tv: wow, that's some type magic...
type Row = Parameters<VDataTableServer['$slots']['item']>[0];

function handleRowClick(_event: MouseEvent, row: Row) {
  emit('clicked', row.item);
}

function handleRemoveClicked(item: Item) {
  emit('removeClicked', item);
}

defineExpose({
  reload,
});
</script>

<template>
  <VDataTableServer
    :headers="headers"
    :items="items"
    :items-length="items.length"
    :items-per-page="5"
    :loading="isLoading"
    @click:row="handleRowClick"
    @update:options="reload"
  >
    <template #[`item.created_at`]="{ item }">
      <span>{{ new Date(item.created_at).toLocaleString() }}</span>
    </template>
    <template #[`item.actions`]="{ item }">
      <!-- <v-icon color="medium-emphasis" icon="mdi-pencil" size="small" @click="edit(item.id)"></v-icon> -->
      <v-icon
        color="medium-emphasis"
        icon="mdi-delete"
        size="small"
        :disabled="item.user !== session?.user.id"
        @click.stop="handleRemoveClicked(item)"
      />
    </template>
  </VDataTableServer>
</template>

<style scoped></style>
